import React, { useContext, useEffect, useRef, useState } from 'react';

import { Dispatcher } from '$partials/listings';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { Button, Filter, Select } from '$partials/ui';
import { ArrowDownIcon, ArrowRightIcon } from '$partials/icon';
import { Stream } from '$partials/modules';
import { ModalContext } from '$contexts/modal.jsx';
import { FilterModal, MapFilterModal, SortByModal, PropertyAlertModal } from '$partials/modals';
import { formatNumber, formatPrice } from '$utilities/format';
import { InListingBlock, KeyLinksBlock, OffMarketBannerBlock } from '$partials/modules/blocks';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {import('$types/components/pages.d.ts').PageFilters}
 */
const Filters = ({
    amenityFilterOptions,
    amenityFilters,
    areaFilterOptions,
    areaFilters,
    bedroomFilterOptions,
    internalAreaFeetFilterOptions,
    maxPrice,
    minArea,
    minBedroom,
    minPrice,
    periodFilterOptions,
    periodFilters,
    priceFilterOptions,
    setActiveFilter,
    setAmenityFilters,
    setAreaFilters,
    setMaxPrice,
    setMinArea,
    setMinBedroom,
    setMinPrice,
    setPeriodFilters,
}) => {

    return (<div className='flex flex-wrap width-100'>
        <div className='col-12 flex flex-wrap'>
            <div className='col-12 sm-col-6 md-col-3 relative border black'>
                <Select
                    label='Min Price'
                    onChange={(value) => {
                        setMinPrice(value);
                    }}
                    options={[{label:'No min', value:''}].concat(priceFilterOptions?.map(option=> {return {label: formatPrice(option), value: option};}))}
                    value={minPrice}/>
            </div>
            <div className='col-12 sm-col-6 md-col-3 relative border-left border-right border-bottom sm-border-0 sm-border-right sm-border-top sm-border-bottom black'>
                <Select
                    label='Max Price'
                    onChange={(value) => {
                        setMaxPrice(value);
                    }}
                    options={priceFilterOptions?.map(option=> {return {label: formatPrice(option), value: option};}).concat([{label:'No max', value:''}])}
                    value={maxPrice} />
            </div>
            <div className='col-12 sm-col-6 md-col-3 relative border-bottom border-left border-right md-border-0 md-border-right md-border-top md-border-bottom black'>
                <Select
                    label='Min Bedrooms'
                    onChange={(value) => {
                        setMinBedroom(value);
                    }}
                    options={[{label:'No min', value:''}].concat(bedroomFilterOptions?.map(option=> {return {label: option, value: option};}))}
                    value={minBedroom} />
            </div>
            <div className='col-12 sm-col-6 md-col-3 relative border-left border-right border-bottom sm-border-0 sm-border-bottom sm-border-right md-border-0 md-border-right md-border-top md-border-bottom black'>
                <Select
                    label='Min sq. feet'
                    onChange={(value) => {
                        setMinArea(value);
                    }}
                    options={[{label:'No min', value:''}].concat(internalAreaFeetFilterOptions?.map(option=> {return {label: formatNumber(option), value: option};}))}
                    value={minArea} />
            </div>
        </div>
        <div className='col-12 flex flex-wrap black'>
            <div className='col-12 md-col-4 p6 border-bottom border-right border-left'>
                <p className='type-style-5'>Amenities</p>
                <div className='pt4'>
                    {amenityFilterOptions?.map((amenity) => {
                        return <Filter
                            className='mb-2px mr-2px'
                            key={`property-listing-page-${amenity.id}`}
                            onClick={() => {
                                const index = amenityFilters.findIndex(queryAmenity => queryAmenity === amenity.value);
                                if(index === -1) {
                                    setAmenityFilters([...amenityFilters, amenity.value]);
                                } else {
                                    setAmenityFilters(prev => prev.filter((val, idx) => idx !== index));
                                }
                            }}
                            selected={amenityFilters.findIndex(queryAmenity => queryAmenity === amenity.value) !== -1}
                            title={amenity.label}/>;
                    })
                    }
                </div>
            </div>
            <div className='col-12 md-col-4 p6 border-left border-right border-bottom md-border-0 md-border-bottom md-border-right '>
                <p className='type-style-5'>Area</p>
                <div className='pt4'>
                    {areaFilterOptions?.map((area) => {
                        return <Filter
                            className='mb-2px mr-2px'
                            key={`property-listing-page-${area.id}`}
                            onClick={() => {
                                const index = areaFilters.findIndex(queryArea => queryArea === area.value);
                                if(index === -1) {
                                    setAreaFilters([...areaFilters, area.value]);
                                } else {
                                    setAreaFilters(prev => prev.filter((val, idx) => idx !== index));
                                }
                            }}
                            selected={areaFilters.findIndex(queryArea => queryArea === area.value) !== -1}
                            title={area.label}/>;
                    })
                    }
                </div>
            </div>
            <div className='col-12 md-col-4 p6 border-left border-right border-bottom md-border-0 md-border-bottom md-border-right'>
                <p className='type-style-5'>Period</p>
                <div className='pt4'>
                    {periodFilterOptions?.map((period) => {
                        return <Filter
                            className='mb-2px mr-2px'
                            key={`property-listing-page-${period.id}`}
                            onClick={() => {
                                const index = periodFilters.findIndex(queryPeriod => queryPeriod === period.value);
                                if(index === -1) {
                                    setPeriodFilters([...periodFilters, period.value]);
                                } else {
                                    setPeriodFilters(prev => prev.filter((val, idx) => idx !== index));
                                }
                            }}
                            selected={periodFilters.findIndex(queryPeriod => queryPeriod === period.value) !== -1}
                            title={period.label}/>;
                    })
                    }
                </div>
            </div>
        </div>
        <div className='col-12 flex justify-end'>
            <Button
                as = 'button'
                className = 'bg-black white type-style-5 max-width-350px'
                look = 'fill'
                onClick={() => {setActiveFilter('');}}
                renderIcon = {<ArrowRightIcon/>}>
                View Results
            </Button>
        </div>

    </div>
    );
};

/**
 * @type {import('$types/components/pages.d.ts').PropertyListingPage}
 */
const PropertyListingPage = (props) => {
    const page = props.data?.page || {};
    const propertyAlert = props.data?.propertyAlert || {};

    const [ isMapOpen, setIsMapOpen ] = useState(false);
    const [ activeFilter, setActiveFilter ] = useState('');
    const [ amenityFilters, setAmenityFilters ] = useState(page.items?.filters?.amenities || []);
    const [ areaFilters, setAreaFilters ] = useState(page.items?.filters?.areas || []);
    const [ periodFilters, setPeriodFilters ] = useState(page.items?.filters?.periods) || [];
    const [ orderBy, setOrderBy ] = useState(page.items?.filters?.orderBy || '');
    const [ maxPrice, setMaxPrice ] = useState(parseInt(page.items?.filters?.maxPrice) || '');
    const [ minPrice, setMinPrice ] = useState(parseInt(page.items?.filters?.minPrice) || '');
    const [ minBedroom, setMinBedroom ] = useState(parseInt(page.items?.filters?.minBedroom) || '');
    const [ minArea, setMinArea ] = useState(parseInt(page.items?.filters?.minArea) || '');

    const [ stickyBtn, setStickyBtn ] = useState(false);
    const [ stickyBtnSM, setStickyBtnSM ] = useState(false);
    const [ stickyBtnWidth, setStickyBtnWidth ] = useState(0);
    const [ scrollDirection, setScrollDirection ] = useState('up');
    const [ btnInitPos, setBtnInitPos ] = useState(0);
    const [ btnSMInitPos, setBtnSMInitPos ] = useState(0);
    const stickyBtnRef = useRef();
    const stickyBtnSMRef = useRef();

    useEffect(() => {
        setBtnInitPos(window?.scrollY + stickyBtnRef.current?.getBoundingClientRect()?.top);
    }, [stickyBtnRef.current]);

    useEffect(() => {
        setStickyBtnWidth(stickyBtnSMRef.current?.clientWidth);
        setBtnSMInitPos(window?.scrollY + stickyBtnSMRef.current?.getBoundingClientRect()?.top);
    }, [stickyBtnSMRef.current]);

    useEffect(() => {
        const threshold = 4;

        let prevY = window.scrollY;
        let tick = false;

        const updateScrollDirection = () => {
            const currentY = window.scrollY;

            if (Math.abs(currentY - prevY) < threshold) {
                tick = false;
                return;
            }

            setScrollDirection(currentY > prevY ? 'down' : 'up');


            if((currentY > btnInitPos) && btnInitPos > 0) {
                setStickyBtn(true);
            } else {
                setStickyBtn(false);
            }

            if((currentY > btnSMInitPos) && btnSMInitPos > 0) {
                setStickyBtnSM(true);
            } else {
                setStickyBtnSM(false);
            }

            prevY = (currentY > 0) ?  currentY : 0;
            tick = false;
        };

        const handleScroll = () => {
            if (!tick) {
                window.requestAnimationFrame(updateScrollDirection);
                tick = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrollDirection]);

    const { handleModal } = useContext(ModalContext);

    const resetFilters = () => {
        setAmenityFilters([]);
        setAreaFilters([]);
        setPeriodFilters([]);
        setOrderBy('');
        setMaxPrice('');
        setMinPrice('');
        setMinArea('');
        setMinBedroom('');
    };

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        page: page.items?.pageInfo?.page || 1,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            'orderBy': orderBy,
            'maxPrice': maxPrice,
            'minPrice': minPrice,
            'minBedroom': minBedroom,
            'minArea': minArea,
            'amenities': amenityFilters,
            'areas': areaFilters,
            'periods': periodFilters
        }
    });

    const inListingItems = page.midListingModules.filter((item) => item.__typename === 'InListingBlock');
    const bannerItems = page.midListingModules.filter((item) => (item.__typename === 'KeyLinksBlock' || item.__typename === 'OffMarketBannerBlock'));
    const backgroundBlocksOne = bannerItems[0];
    const backgroundBlocksTwo = bannerItems[1];

    let firstBatchItems;
    let secondBatchItems;
    let remainingItems;

    const lastIndexOf = (arr) => {
        for (let i = arr.length - 1; i >= 0; i--) {
            if (arr[i].node?.offMarket === true || arr[i].__typename === 'InListingBlock') {
                return i;
            }
        }
        return null;
    };

    const sortItems = () => {
        const sorted = [];
        const skipped = [];
        listing?.items?.map((item) => {
            const lastOffMarketIndex = lastIndexOf(sorted);
            const patternIsReady = lastOffMarketIndex === null ? true : lastOffMarketIndex < sorted.length - (lastOffMarketIndex % 2 === 0 ? 4 : 2);
            const slottedInListing = sorted.filter(items => items.__typename === 'InListingBlock');
            const hasInListingLeft = (slottedInListing.length !== inListingItems.length);

            if (sorted.length < 3) {
                if (item.node.offMarket) {
                    skipped.push(item);
                } else {
                    sorted.push(item);
                }
            } else {
                if (item.node.offMarket) {
                    // add In Listing module [0] && [1] into pattern
                    if (lastOffMarketIndex === 3 && sorted.length < 10 && inListingItems?.[0] && hasInListingLeft && patternIsReady) {
                        sorted.push(inListingItems[0]);
                        skipped.push(item);
                    } else if (lastOffMarketIndex === 11 && inListingItems?.[1] && hasInListingLeft && patternIsReady) {
                        sorted.push(inListingItems[1]);
                        skipped.push(item);
                    }
                    // no inListing module
                    else {
                        if (skipped.length > 0 && patternIsReady) {
                            sorted.push(skipped[0]);
                            skipped.shift();
                            skipped.push(item);
                        }
                        // check if last items contain off market
                        else if (!patternIsReady) {
                            // skip and store in remaining
                            skipped.push(item);
                        }
                        // !skipped.length && patternIsReady
                        else {
                            sorted.push(item);
                        }
                    }
                } else {
                    // if no off market in slot of pattern
                    // push inlisting
                    if (!skipped.length && hasInListingLeft && patternIsReady) {
                        // inListing to take first slot
                        if (sorted.length === 3 && lastOffMarketIndex === null && inListingItems?.[0]) {
                            sorted.push(inListingItems[0]);
                            sorted.push(item);
                        }
                        // inListing to take second slot
                        else if (sorted.length > 5 && lastOffMarketIndex === 3 && !slottedInListing.length && inListingItems?.[0]) {
                            sorted.push(inListingItems[0]);
                            sorted.push(item);
                        }
                        else if (!slottedInListing.length) {
                            sorted.push(inListingItems[0]);
                            sorted.push(item);
                        }
                        else if (inListingItems.length === 2 && slottedInListing.length === 1) {
                            sorted.push(inListingItems[1]);
                            sorted.push(item);
                        }
                    }
                    // check if any off market have been skipped
                    // only push in if it should be next in pattern
                    else if (skipped.length > 0 && patternIsReady) {
                        if (lastOffMarketIndex === 3 && sorted.length < 10 && inListingItems?.[0] && hasInListingLeft) {
                            sorted.push(inListingItems[0]);
                            sorted.push(item);
                        } else if (lastOffMarketIndex === 11 && inListingItems?.[1] && hasInListingLeft ) {
                            sorted.push(inListingItems[1]);
                            sorted.push(item);
                        } else {
                            sorted.push(skipped[0]);
                            skipped.shift();
                            sorted.push(item);
                        }
                    }
                    else {
                        sorted.push(item);
                    }
                }
            }

        });
        if (listing.page === listing.totalPages) {
            return sorted.concat(skipped);
        }
        return sorted;
    };

    const formattedItems = page?.priceOrderedView ? listing?.items : sortItems();

    if (bannerItems.length > 1) {
        firstBatchItems = formattedItems.slice?.(0, 4);
        secondBatchItems = formattedItems.slice?.(4, 12);
        remainingItems = formattedItems.slice?.(12);
    } else {
        firstBatchItems = formattedItems.slice?.(0, 4);
        remainingItems = formattedItems.slice?.(4);
    }

    return (
        <main className='container'>
            <AjaxListingContext.Provider value={listing}>
                <MapFilterModal
                    {...props}
                    filters={{
                        'orderBy': orderBy,
                        'maxPrice': maxPrice,
                        'minPrice': minPrice,
                        'minBedroom': minBedroom,
                        'minArea': minArea,
                        'amenities': amenityFilters,
                        'areas': areaFilters,
                        'periods': periodFilters,
                        'perPage': listing?.totalItems || page.items?.total,
                    }}
                    handleClose={() => setIsMapOpen(false)}
                    isNavOpen={isMapOpen}
                    listing={listing}
                    resetFilters={resetFilters} />
                {/* Desktop */}
                <div className='hide md-show'>
                    <div className='col-12 flex flex-wrap sm-flex-no-wrap'>
                        <div
                            className='flex-grow py6 md-py7 md-pr0 bg-white flex items-center pl5 md-pl6 border-right border-bottom-2px border-top grey'
                            style={{width: 'calc(50% + 20px)'}}>
                            <h2 className='type-style-2 black'>{page.title}</h2>
                        </div>
                        <div className='col-12 sm-col-6 flex flex-wrap border-left border-top grey'>
                            <div className={`flex-basis-50 border-right flex flex-grow${activeFilter === 'filter' ? '' :' border-bottom-2px'}`}>
                                <Button
                                    as='button'
                                    className='bg-white black type-style-5 items-center'
                                    look='fill-narrow'
                                    onClick={() => {
                                        if(activeFilter === 'filter'){
                                            setActiveFilter('');
                                        } else {
                                            setActiveFilter('filter');
                                        }
                                    }}
                                    renderIcon = {activeFilter === 'filter' ? <ArrowDownIcon /> : <ArrowRightIcon />}>
                                        Filter
                                </Button>
                            </div>
                            <div className='flex-basis-50 border-right border-left flex flex-grow border-bottom-2px'>
                                <Button
                                    as='button'
                                    className='bg-white black type-style-5 items-center'
                                    look='fill-narrow'
                                    onClick={() => setIsMapOpen(true)}
                                    renderIcon={<ArrowRightIcon />}>
                                        Map
                                </Button>
                            </div>
                            <div className={`flex-basis-50 flex flex-grow border-right ${activeFilter === 'sortBy' ? '' :' border-bottom-2px'}`}>
                                <Button
                                    as='button'
                                    className='bg-white black type-style-5 items-center'
                                    look='fill-narrow'
                                    onClick={() => {
                                        if (activeFilter === 'sortBy') {
                                            setActiveFilter('');
                                        } else {
                                            setActiveFilter('sortBy');
                                        }
                                    }}
                                    renderIcon={activeFilter === 'sortBy' ? <ArrowDownIcon /> : <ArrowRightIcon />}>
                                Sort By
                                </Button>
                            </div>
                            <div
                                className='flex-basis-50 flex flex-grow border-left border-bottom-2px'
                                ref={stickyBtnSMRef}>
                                <Button
                                    as='button'
                                    className={`bg-peach black type-style-5 items-center ${stickyBtnSM ? 'fixed bottom-0 z4 container' : ''}`}
                                    look='fill-narrow'
                                    onClick={() => handleModal(
                                        <PropertyAlertModal
                                            {...propertyAlert}
                                            areaFilterOptions={page.areaFilterOptions}
                                            areaFilters={areaFilters}
                                            hubspot={props.hubspot}
                                            maxPrice={maxPrice}
                                            minPrice={minPrice}
                                            page={page} />)}
                                    renderIcon={<ArrowRightIcon />}
                                    style={{
                                        maxWidth: stickyBtnSM ? stickyBtnWidth : 'none'
                                    }}>
                            Property Alert
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                {activeFilter && (
                    <div className='col-12 bg-white border-bottom grey flex px6 py9'>
                        {activeFilter === 'sortBy' && (
                            <>
                                {page.orderByOptions?.map((order, idx) => {
                                    return (
                                        <button
                                            className={`form-field-reset black cursor-pointer ${idx === 0 ? 'border' : 'border-right border-top border-bottom'} ${orderBy === order.value ? 'bg-yellow' : ''} button inline-flex width-100 pl5 md-pl6 pr6 pt6 pb6 md-pt5 md-pb5`}
                                            key={order.id}
                                            onClick={() => {
                                                setOrderBy(order.value);
                                                setActiveFilter('');
                                            }}
                                            type='button'>
                                            <span className='flex flex-col width-100'>
                                                <p className='type-style-5 align-left py2'>{order.category}</p>
                                                <div className='flex justify-between items-center'>
                                                    <h3 className='type-style-3 align-left'>{order.label}</h3>
                                                    <span
                                                        aria-hidden={true}
                                                        className='font-0 line-height-0 p3 md-p5'>
                                                        <ArrowRightIcon />
                                                    </span>
                                                </div>
                                            </span>
                                        </button>
                                    );
                                })}
                            </>
                        )}
                        {activeFilter === 'filter' && (
                            <Filters
                                amenityFilterOptions={page.amenityFilterOptions}
                                amenityFilters={amenityFilters}
                                areaFilterOptions={page.areaFilterOptions}
                                areaFilters={areaFilters}
                                bedroomFilterOptions={page.bedroomFilterOptions}
                                internalAreaFeetFilterOptions={page.internalAreaFeetFilterOptions}
                                maxPrice={maxPrice}
                                minArea={minArea}
                                minBedroom={minBedroom}
                                minPrice={minPrice}
                                periodFilterOptions={page.periodFilterOptions}
                                periodFilters={periodFilters}
                                priceFilterOptions={page.priceFilterOptions}
                                setActiveFilter={setActiveFilter}
                                setAmenityFilters={setAmenityFilters}
                                setAreaFilters={setAreaFilters}
                                setMaxPrice={setMaxPrice}
                                setMinArea={setMinArea}
                                setMinBedroom={setMinBedroom}
                                setMinPrice={setMinPrice}
                                setPeriodFilters={setPeriodFilters} />
                        )}
                    </div>
                )}

                {/* Tablet / Mobile */}
                <div className='md-hide'>
                    <div className='col-12 flex flex-wrap sm-flex-no-wrap'>
                        <div
                            className='flex-grow py6 md-py7 md-pr0 bg-white flex items-center pl5 md-pl6 sm-border-right border-bottom-2px border-top grey'
                            style={{width: 'calc(50% + 20px)'}}>
                            <h2 className='type-style-2 black'>{page.title}</h2>
                        </div>
                        <div
                            className='col-12 flex flex-grow'
                            ref={stickyBtnRef}>
                            <Button
                                as='button'
                                className={`bg-peach black type-style-5 items-center ${stickyBtn ? 'fixed bottom-0 z4 container' : ''}`}
                                look='fill'
                                onClick={() => handleModal(
                                    <PropertyAlertModal
                                        {...propertyAlert}
                                        areaFilterOptions={page.areaFilterOptions}
                                        areaFilters={areaFilters}
                                        hubspot={props.hubspot}
                                        maxPrice={maxPrice}
                                        minPrice={minPrice}
                                        page={page} />)}
                                renderIcon={<ArrowRightIcon />}>
                            Property Alert
                            </Button>
                        </div>
                        <div className='col-12 sm-col-6 flex border-left border-top grey'>
                            <div className={`border-right flex flex-grow${activeFilter === 'filter' ? '' :' border-bottom-2px'}`}>
                                <Button
                                    as='button'
                                    className='bg-white black type-style-5 items-center'
                                    look='fill'
                                    onClick={() => {
                                        handleModal(
                                            <FilterModal
                                                amenityFilterOptions={page.amenityFilterOptions}
                                                amenityFilters={amenityFilters}
                                                areaFilterOptions={page.areaFilterOptions}
                                                areaFilters={areaFilters}
                                                bedroomFilterOptions={page.bedroomFilterOptions}
                                                internalAreaFeetFilterOptions={page.internalAreaFeetFilterOptions}
                                                maxPrice={maxPrice}
                                                minArea={minArea}
                                                minBedroom={minBedroom}
                                                minPrice={minPrice}
                                                periodFilterOptions={page.periodFilterOptions}
                                                periodFilters={periodFilters}
                                                priceFilterOptions={page.priceFilterOptions}
                                                setActiveFilter={setActiveFilter}
                                                setAmenityFilters={setAmenityFilters}
                                                setAreaFilters={setAreaFilters}
                                                setMaxPrice={setMaxPrice}
                                                setMinArea={setMinArea}
                                                setMinBedroom={setMinBedroom}
                                                setMinPrice={setMinPrice}
                                                setPeriodFilters={setPeriodFilters}>
                                            </FilterModal>);
                                    }}
                                    renderIcon = {<ArrowDownIcon />}>
                            Filter
                                </Button>
                            </div>
                            <div className='border-right border-left flex flex-grow border-bottom-2px'>
                                <Button
                                    as='button'
                                    className='bg-white black type-style-5 items-center'
                                    look='fill'
                                    onClick={() => setIsMapOpen(true)}
                                    renderIcon={<ArrowDownIcon />}>
                                Map
                                </Button>
                            </div>
                            <div className={`border-left flex flex-grow${activeFilter === 'sortBy' ? '' :' border-bottom-2px'}`}>
                                <Button
                                    as='button'
                                    className='bg-white black type-style-5 items-center'
                                    look='fill'
                                    onClick={() => {
                                        handleModal(<SortByModal
                                            orderBy={orderBy}
                                            orderByOptions={page.orderByOptions}
                                            setActiveFilter={setActiveFilter}
                                            setOrderBy={setOrderBy} />);
                                    }}
                                    renderIcon={<ArrowDownIcon />}>
                                Sort By
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex flex-wrap col-12 property-item-row-gap'>
                    {/* No items found, show only first Banner module */}
                    {listing.items.length === 0 && (
                        <>
                            <div className='col-12 border-top border-bottom bg-white grey py9 px5 md-px6'>
                                <p className='type-style-5 mb3 black'>Please choose different filters</p>
                                <h2 className='type-style-2 black'>No Properties Found</h2>
                                <div className='col-12 md-col-4'>
                                    <Button
                                        as='button'
                                        className='bg-none type-style-5'
                                        iconClassName='bg-black white button border-radius-100px p3 md-p5'
                                        look='underline'
                                        onClick={() => {
                                            resetFilters();
                                        }}
                                        renderIcon={<ArrowRightIcon/>}>
                                            Reset Filters
                                    </Button>
                                </div>
                            </div>
                            {/* If Banner */}
                            {backgroundBlocksOne && (
                                <aside className='col-12'>
                                    <div>
                                        {backgroundBlocksOne.__typename === 'KeyLinksBlock'&& <KeyLinksBlock {...backgroundBlocksOne}/>}
                                        {backgroundBlocksOne.__typename === 'OffMarketBannerBlock' && (
                                            <OffMarketBannerBlock
                                                areaFilterOptions={page.areaFilterOptions}
                                                parentPageType={page.__typename}
                                                {...backgroundBlocksOne}/>
                                        )}
                                    </div>
                                </aside>
                            )}
                        </>
                    )}

                    {listing.items.length > 0 && (
                        <>
                            {firstBatchItems.map((item) => {
                                if (item.__typename === 'InListingBlock') {
                                    return (
                                        <div
                                            className={'hide sm-show col-12 sm-col-6 expand-interaction bg-white property-item'}>
                                            <InListingBlock
                                                hubspot={props.hubspot}
                                                page={page}
                                                {...item} />
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        className={'col-12 sm-col-6 expand-interaction bg-white property-item'}
                                        key={item?.node?.id}>
                                        <Dispatcher
                                            areaFilterOptions={page.areaFilterOptions}
                                            item={item?.node}
                                            offMarketLink={props?.data?.offMarket?.offMarketBoxLink}/>
                                    </div>
                                );
                            })}
                            {/* If Banner module */}
                            {backgroundBlocksOne && (
                                <aside className='col-12'>
                                    <div>
                                        {backgroundBlocksOne.__typename === 'KeyLinksBlock'&& <KeyLinksBlock {...backgroundBlocksOne}/>}
                                        {backgroundBlocksOne.__typename === 'OffMarketBannerBlock' && (
                                            <OffMarketBannerBlock
                                                areaFilterOptions={page.areaFilterOptions}
                                                parentPageType={page.__typename}
                                                {...backgroundBlocksOne}/>
                                        )}
                                    </div>
                                </aside>
                            )}
                            {secondBatchItems?.map((item) => {
                                if (item.__typename === 'InListingBlock') {
                                    return (
                                        <div
                                            className={'hide sm-show col-12 sm-col-6 expand-interaction bg-white property-item'}>
                                            <InListingBlock
                                                hubspot={props.hubspot}
                                                page={page}
                                                {...item} />
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        className={'col-12 sm-col-6 expand-interaction bg-white property-item'}
                                        key={item?.node?.id}>
                                        <Dispatcher
                                            areaFilterOptions={page.areaFilterOptions}
                                            item={item?.node}
                                            offMarketLink={props?.data?.offMarket?.offMarketBoxLink}/>
                                    </div>
                                );
                            })}
                            {/* If second Banner module */}
                            {secondBatchItems?.length > 0 && backgroundBlocksTwo && (
                                <aside className='col-12'>
                                    <div>
                                        {backgroundBlocksTwo.__typename === 'KeyLinksBlock'&& <KeyLinksBlock {...backgroundBlocksTwo}/>}
                                        {backgroundBlocksTwo.__typename === 'OffMarketBannerBlock' && (
                                            <OffMarketBannerBlock
                                                areaFilterOptions={page.areaFilterOptions}
                                                parentPageType={page.__typename}
                                                {...backgroundBlocksTwo}/>
                                        )}
                                    </div>
                                </aside>
                            )}
                            {remainingItems.map((item) => {
                                if (item.__typename === 'InListingBlock') {
                                    return (
                                        <div
                                            className={'hide sm-show col-12 sm-col-6 expand-interaction bg-white property-item'}>
                                            <InListingBlock
                                                hubspot={props.hubspot}
                                                page={page}
                                                {...item} />
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        className={'col-12 sm-col-6 expand-interaction bg-white property-item'}
                                        key={item?.node?.id}>
                                        <Dispatcher
                                            areaFilterOptions={page.areaFilterOptions}
                                            item={item?.node}
                                            offMarketLink={props?.data?.offMarket?.offMarketBoxLink}/>
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {listing?.page < listing?.totalPages && (
                        <InfinitePagination>
                            <div>
                                <p className='type-style-5 px5 py7'>Loading</p>
                            </div>
                        </InfinitePagination>
                    )}

                    {/* remaining modules */}
                    {page.modules &&
                        <div className='col-12'>
                            <Stream
                                modules={page.modules}
                                namespace='property-listing' />
                        </div>
                    }

                </div>
            </AjaxListingContext.Provider>
        </main>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PropertyListingPage;
